/**
 * InvitationFormSection Component - Orchestrator
 *
 * Manages three views:
 * 1. Default: Header + input row (no invitations)
 * 2. Compact: Header + input + summary (has invitations, not expanded)
 * 3. Expanded: Full list with input at top and close button
 *
 * @module components/workspace/WorkspaceCreationForm/components/InvitationFormSection
 */

import { memo } from 'react';
import { IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { InvitationInput } from './components/InvitationInput';
import { CompactInvitationSummary } from './components/CompactInvitationSummary';
import { ExpandedInvitationList } from './components/ExpandedInvitationList';
import { SECTION_ICON_SIZE } from '@/constants/ui';
import type { InvitationFormSectionProps } from './types';
import styles from './InvitationFormSection.module.css';

/**
 * Section header component - visible in default and compact views.
 * Displays green icon, title/subtitle, and optional badge.
 */
function SectionHeader() {
  const { t } = useTranslation();
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionIcon}>
        <IconUsers size={SECTION_ICON_SIZE} />
      </div>
      <div className={styles.sectionText}>
        <div className={styles.sectionTitleRow}>
          <div className={styles.sectionTitle}>
            {t('workspace.form.invitationsTitle')}
          </div>
          <span className={styles.optionalBadge}>{t('common.optional')}</span>
        </div>
        <div className={styles.sectionSubtitle}>
          {t('workspace.form.invitationsDescription')}
        </div>
      </div>
    </div>
  );
}

export const InvitationFormSection = memo(function InvitationFormSection({
  pendingInvitations,
  onAdd,
  onRemove,
  onRoleChange,
  isSubmitting = false,
  isExpanded = false,
  onToggleExpanded,
}: InvitationFormSectionProps) {
  const hasInvitations = pendingInvitations.length > 0;

  // Expanded view - full list with input and close button
  if (isExpanded) {
    return (
      <div className={styles.expandedContainer}>
        <ExpandedInvitationList
          invitations={pendingInvitations}
          onAdd={onAdd}
          onRemove={onRemove}
          onRoleChange={onRoleChange}
          onClose={() => onToggleExpanded?.()}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  // Compact view - header + input + summary (when has invitations)
  if (hasInvitations) {
    return (
      <div className={styles.compactContainer}>
        <div className={styles.contentStack}>
          <SectionHeader />
          <InvitationInput
            onAdd={onAdd}
            pendingInvitations={pendingInvitations}
            isSubmitting={isSubmitting}
          />
          <CompactInvitationSummary
            invitations={pendingInvitations}
            onExpand={() => onToggleExpanded?.()}
            disabled={isSubmitting}
          />
        </div>
      </div>
    );
  }

  // Default view - header + input row (no invitations)
  return (
    <div className={styles.defaultContainer}>
      <div className={styles.contentStack}>
        <SectionHeader />
        <InvitationInput
          onAdd={onAdd}
          pendingInvitations={pendingInvitations}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
});
